<template>
  <NPopover
    ref="popoverRef"
    :animated="false"
    class="formatter-field-select"
    :on-clickoutside="handleClickOutside"
    placement="bottom"
    :show="showPopover"
    :show-arrow="false"
    trigger="manual"
    :width="300"
  >
    <template #trigger>
      <CButton
        class="formatter-field-select__trigger"
        :themeOverrides="themeOverrides?.[selectedOverride]"
        @click="handleTriggerClick"
      >
        <CIcon class="mr-1" icon="wand-magic-sparkles" size="16px" />
        <span class="ml-1 mr-1 text-xs">Format Field</span>
        <CIcon class="ml-1" icon="chevron-down" size="12px" />
      </CButton>
    </template>

    <div v-if="step === 'formatter'" class="py-2 px-3">
      <div class="text-sm font-bold mb-2">Select Format First</div>
      <NTreeSelect
        ref="formatterSelectRef"
        block-line
        filterable
        :on-update:value="onSelectFormatter"
        :options="formatterOptions"
        :override-default-node-click-behavior="override"
        :placeholder="t('canvasUI.CHandlebarsHelperTreeSelect.searchPlaceholder')"
      />
    </div>

    <div v-else-if="step === 'field'" class="py-2 px-3">
      <div class="flex items-center justify-between mb-2">
        <span class="text-sm font-bold">Now Select CRM Field</span>
        <CButton class="text-xs" size="small" @click="resetSelection">
          <CIcon icon="arrow-left" size="12px" />
          <span class="ml-1">Back</span>
        </CButton>
      </div>
      <NTreeSelect
        ref="fieldSelectRef"
        block-line
        filterable
        :on-update:value="onSelectField"
        :options="fieldOptions"
        :override-default-node-click-behavior="override"
        placeholder="Search field..."
      />
    </div>
  </NPopover>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick } from 'vue'
import { NTreeSelect, NPopover, type TreeSelectOption, type TreeSelectOverrideNodeClickBehavior } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { transformToTree } from '@lib/util/tree-transformation.util'

const props = defineProps<{
  crmData: Record<string, any>
  themeOverrides?: Record<string, any>
}>()

const emit = defineEmits<{ select: [value: string] }>()
const { t } = useI18n()

const popoverRef = ref<InstanceType<typeof NPopover> | null>(null)
const formatterSelectRef = ref<InstanceType<typeof NTreeSelect> | null>(null)
const fieldSelectRef = ref<InstanceType<typeof NTreeSelect> | null>(null)
const selectedOverride = ref<'default' | 'active'>('default')
const showPopover = ref(false)
const step = ref<'formatter' | 'field'>('formatter')
const selectedFormatter = ref<string>('')

const override: TreeSelectOverrideNodeClickBehavior = ({ option }) => {
  if (option.children) {
    return 'toggleExpand'
  }
  return 'default'
}

// CRM fields tree
const treeData = computed(() => transformToTree(props.crmData ?? {}))
const fieldOptions = computed(() => treeData.value.options)
const arrayDict = computed(() => treeData.value.arrayDict)

// Format helpers organized by category
const formatterHelpers = [
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Format'),
    items: [
      {
        name: 'numberFormat',
        template: '{{numberFormat number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.numberFormat'),
      },
      {
        name: 'currency',
        template: '{{currency number "USD"}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.currency'),
      },
      {
        name: 'percentage',
        template: '{{percentage number 2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.percentage'),
      },
      {
        name: 'json',
        template: '{{json object}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.json'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.String'),
    items: [
      {
        name: 'lowercase',
        template: '{{lowercase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.lowercase'),
      },
      {
        name: 'uppercase',
        template: '{{uppercase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.uppercase'),
      },
      {
        name: 'capitalize',
        template: '{{capitalize string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.capitalize'),
      },
      {
        name: 'titleCase',
        template: '{{titleCase string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.titleCase'),
      },
      {
        name: 'trim',
        template: '{{trim string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.trim'),
      },
      {
        name: 'substring',
        template: '{{substring string start end}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.substring'),
      },
      {
        name: 'replace',
        template: '{{replace string pattern replacement}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.replace'),
      },
      {
        name: 'concat',
        template: '{{concat string1 string2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.concat'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.CRM'),
    items: [
      {
        name: 'phoneFormat',
        template: '{{phoneFormat phone}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.phoneFormat'),
      },
      {
        name: 'emailLink',
        template: '{{{emailLink email}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.emailLink'),
      },
      {
        name: 'phoneLink',
        template: '{{{phoneLink phone}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.phoneLink'),
      },
      {
        name: 'initials',
        template: '{{initials name}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.initials'),
      },
      {
        name: 'pluralize',
        template: '{{pluralize count "item" "items"}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.pluralize'),
      },
      {
        name: 'fileSize',
        template: '{{fileSize bytes}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.fileSize'),
      },
      {
        name: 'truncate',
        template: '{{truncate text 50}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.truncate'),
      },
      {
        name: 'slug',
        template: '{{slug title}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.slug'),
      },
      {
        name: 'emailDomain',
        template: '{{emailDomain email}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.emailDomain'),
      },
      {
        name: 'mapLink',
        template: '{{{mapLink address}}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.mapLink'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Date'),
    items: [
      {
        name: 'formatDate',
        template: '{{formatDate dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.formatDate'),
      },
      {
        name: 'formatDateLong',
        template: '{{formatDateLong dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.formatDateLong'),
      },
      { name: 'now', template: '{{now}}', description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.now') },
      {
        name: 'year',
        template: '{{year dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.year'),
      },
      {
        name: 'month',
        template: '{{month dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.month'),
      },
      {
        name: 'day',
        template: '{{day dateValue}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.day'),
      },
      {
        name: 'addDays',
        template: '{{addDays dateValue 7}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.addDays'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Math'),
    items: [
      {
        name: 'add',
        template: '{{add number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.add'),
      },
      {
        name: 'subtract',
        template: '{{subtract number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.subtract'),
      },
      {
        name: 'multiply',
        template: '{{multiply number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.multiply'),
      },
      {
        name: 'divide',
        template: '{{divide number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.divide'),
      },
      {
        name: 'mod',
        template: '{{mod number1 number2}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.mod'),
      },
      {
        name: 'floor',
        template: '{{floor number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.floor'),
      },
      {
        name: 'ceil',
        template: '{{ceil number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.ceil'),
      },
      {
        name: 'round',
        template: '{{round number}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.round'),
      },
      {
        name: 'toFixed',
        template: '{{toFixed number digits}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.toFixed'),
      },
    ],
  },
  {
    category: t('canvasUI.CHandlebarsHelperTreeSelect.categories.Encoding'),
    items: [
      {
        name: 'encodeURI',
        template: '{{encodeURI string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.encodeURI'),
      },
      {
        name: 'decodeURI',
        template: '{{decodeURI string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.decodeURI'),
      },
      {
        name: 'escapeHTML',
        template: '{{escapeHTML string}}',
        description: t('canvasUI.CHandlebarsHelperTreeSelect.descriptions.escapeHTML'),
      },
    ],
  },
]

// Transform formatters into TreeSelectOption structure
function transformFormattersToOptions(): TreeSelectOption[] {
  return formatterHelpers.map((category) => ({
    label: category.category,
    key: `category-${category.category}`,
    children: category.items.map((item) => ({
      label: item.name,
      key: `${category.category}-${item.name}`,
      description: item.description,
      template: item.template,
    })),
  }))
}

const formatterOptions = computed(() => transformFormattersToOptions())

function extractFormatterName(template: string): string {
  // Extract the formatter name from templates like "{{numberFormat number}}"
  const match = template.match(/{{([a-zA-Z]+)/)
  return match ? match[1] : ''
}

function onSelectFormatter(selection: string) {
  const [categoryName, helperName] = selection.split('-')
  const category = formatterHelpers.find((cat) => cat.category === categoryName)

  if (category) {
    const helper = category.items.find((item) => item.name === helperName)
    if (helper) {
      selectedFormatter.value = extractFormatterName(helper.template)
      step.value = 'field'
      nextTick(() => {
        fieldSelectRef.value?.$el.querySelector('.formatter-field-select.n-popover .n-base-selection')?.click()
      })
    }
  }
}

function onSelectField(selection: string) {
  popoverRef.value?.setShow(false)
  showPopover.value = false
  selectedOverride.value = 'default'

  if (arrayDict.value[selection]) {
    // Array field handling could be improved here
    const formattedTemplate = `{{#each ${selection}}}
  {{${selectedFormatter.value} this}}
{{/each}}`
    emit('select', formattedTemplate)
  } else {
    // Standard field with formatter
    const formattedTemplate = `{{${selectedFormatter.value} ${selection}}}`
    emit('select', formattedTemplate)
  }

  // Reset for next use
  resetSelection()
}

function resetSelection() {
  step.value = 'formatter'
  selectedFormatter.value = ''
  nextTick(() => {
    formatterSelectRef.value?.$el.querySelector('.formatter-field-select.n-popover .n-base-selection')?.click()
  })
}

async function handleShow(show: boolean) {
  if (show) {
    await nextTick()
    const element: HTMLElement | null = formatterSelectRef.value?.$el.querySelector(
      '.formatter-field-select.n-popover .n-base-selection',
    )
    element?.click()
  }
}

function handleTriggerClick() {
  showPopover.value = !showPopover.value
  handleShow(showPopover.value)
  selectedOverride.value = 'active'
}

function handleClickOutside() {
  // forcing this event to be executed after handleTriggerClick
  setTimeout(() => {
    showPopover.value = false
    selectedOverride.value = 'default'
    resetSelection()
  }, 0)
}
</script>

<style lang="scss" scoped>
.formatter-field-select {
  :deep(.n-tree-select-menu .n-base-select-option) {
    display: flex;
    flex-direction: column;

    .n-base-select-option__content {
      display: flex;
      align-items: center;
    }
  }

  :deep(.n-tree-select-menu .n-base-select-option-body__prefix) {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }
}

.formatter-field-select__trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
}
</style>
